import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { UserBot } from './types';
import { SaveUserBotResult } from 'types/ApiServicesTypes';
import CircularLoader from 'app/design/uiComponents/CircularLoader';
import { useGetMediaQueryUp } from 'app/hooks/useGetMediaQuery';
import Breadcrumbs from 'app/design/uiComponents/Breadcrumbs';
import { PAGES } from 'app/components/Common/Breadcrumb/types';
import { useBreadcrumbManager } from 'app/components/Common/Breadcrumb';
import PageHeader from 'app/design/speedBot/PageHeader';
import YouTubeIcon from '@mui/icons-material/YouTube';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';

import OptionStrategyBuilderForm from './OptionStrategyBuilderForm';
import { getQueryParam } from 'utils/GenericFunctions';
import MuiButton from 'app/design/uiComponents/MuiButton';
import { useState } from 'react';

//Services
import useEditUserBot from 'services/Bots/useEditUserBot';
import useSaveUserBot from 'services/Bots/useSaveUserBot';
import {
  FeatureFlag,
  useIsFeatureFlag,
} from 'app/components/Common/FeatureFlag';
import { FEATURES } from 'types/Feature';
import HelpGuideDialog from './HelpGuideDialog';
import CreateTickFormModal from 'app/components/Menu/CreateTicketFormModal';
import { isUserLoggedIn } from 'utils/auth';
import { CoPresent, SaveAltOutlined } from '@mui/icons-material';
import useGetSiteInfo from 'services/common/useGetSiteInfo';
import ImportStrategyDialog from './ImportStrategyDialog';
import { DropdownFieldValue } from 'app/components/Builder/OptionsStrategy/types';
import useGetOptionSymbols from 'services/Bots/useGetOptionSymbols';
import { signal } from '@preact/signals-react';
import BetaChip from 'app/components/Builder/BetaChip';
export const equityJsonData = signal<DropdownFieldValue[]>([]);

export default function OptionStrategyBuilderContent({
  botId,
  subId,
  userBotDetails,
  isLoading,
  isTemplate,
  botBuilderType,
}: {
  botId: number;
  subId: number;
  isTemplate?: boolean;
  userBotDetails: UserBot | undefined;
  isLoading: boolean;
  botBuilderType: string | undefined;
}) {
  const breadcrumb = useBreadcrumbManager();
  const history = useHistory();
  const { t } = useTranslation();
  const isLgUp = useGetMediaQueryUp('lg');
  const isLoggedIn = isUserLoggedIn();
  const theme = useTheme();
  const source = getQueryParam('source');

  const [open, setOpen] = useState(false);
  const [openReadDialog, setOpenReadDialog] = useState(false);
  const [openImpmortDialog, setOpenImportDialog] = useState(false);
  const [openCreateTicketDialog, setOpenCreateTicketDialog] = useState(false);
  const isHelpDeskInSb = useIsFeatureFlag([FEATURES.IS_HELP_DESK_IN_SB]);
  const isBotDescription = useIsFeatureFlag([FEATURES.IS_BOT_DISCRIPTION]);
  const isImportExportStrategy = useIsFeatureFlag([
    FEATURES.IS_IMPORT_EXPORT_STRATEGY,
  ]);
  const saveUserBotSuccessCallback = (
    res?: SaveUserBotResult,
    name?: string,
  ) => {
    if (res) {
      breadcrumb.push(
        `/bot/${res.bot_id}/backtesting/${subId ? subId : ''}`,
        PAGES.BOT_STRATEGY_BUILDER,
        `/bot-builder/${res.bot_id}/${subId ? subId : ''}?type=option`,
        name as string,
      );
    }
  };
  const isOptionBotBuilderStockSymbols = useIsFeatureFlag([
    FEATURES.IS_OPTION_BOTBUILDER_STOCK_SYMBOLS,
  ]);
  const isShowUSAStockSymbols = useIsFeatureFlag([
    FEATURES.IS_SHOW_US_STOCK_SYMBOLS,
  ]);
  const isShowOptionSymbolFromBackend = useIsFeatureFlag([
    FEATURES.IS_SHOW_OPTION_SYMBOL_FROM_BACKEND,
  ]);
  const parameter =
    botBuilderType == 'option'
      ? isShowUSAStockSymbols
        ? 'USA_optionBuilder'
        : 'ALL_STOCKS_FNO'
      : botBuilderType?.toUpperCase() || 'ALL_STOCKS_FNO';
  const { data, isLoading : isStockSymbolLoaded } = useGetOptionSymbols(
    parameter,
    isOptionBotBuilderStockSymbols,
    isShowUSAStockSymbols,
    isShowOptionSymbolFromBackend,
  );
  const modifiedData = data
    ? (data.data[parameter].Stocks.map(item => ({
        key: item.key || item.value,
        label: item.value,
        tooltip: '',
      })) as DropdownFieldValue[])
    : [];
  equityJsonData.value = modifiedData;
  const mutationAdd = useSaveUserBot(saveUserBotSuccessCallback);
  const mutationEdit = useEditUserBot(botId, saveUserBotSuccessCallback);
  const { data: siteInfo } = useGetSiteInfo();
  const setUserBotData = (userBotData: UserBot) => {
    // Call api here
    if (userBotData) {
      const objReq = userBotData;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      //  userBotDetails&&userBotDetails.sb_strategy&&userBotDetails.sb_strategy.sb_opt_strategy.strategy_sets.map(x=>x.indicator_extras)
      if (
        botId &&
        objReq.sb_strategy &&
        userBotDetails &&
        userBotDetails.sb_strategy
      ) {
        objReq.sb_strategy.start_date = userBotDetails.sb_strategy.start_date;
        objReq.sb_strategy.end_date = userBotDetails.sb_strategy.end_date;

        objReq.sb_strategy.initial_capital =
          userBotDetails.sb_strategy.initial_capital;
        objReq.sb_strategy.trading_start_time =
          userBotDetails.sb_strategy.trading_start_time;
        objReq.sb_strategy.trading_stop_time =
          userBotDetails.sb_strategy.trading_stop_time;
      }
      objReq.is_backtest = false;

      if (isTemplate) objReq.template_id = botId;
      if (!isBotDescription) delete objReq.description;

      if (botId && !isTemplate) mutationEdit.mutate(objReq);
      else mutationAdd.mutate(objReq);
    }
  };
  const params = new URLSearchParams({ ['pytype']: 'mybots' });
  return (
    <Box>
      <CircularLoader
        open={isLoading || mutationAdd.isLoading || mutationEdit.isLoading}
      />
      <Box>
        <PageHeader
          variant="inactive"
          buttonText={
            userBotDetails && Object.keys(userBotDetails).length > 0
              ? t('buttons.backtesting')
              : source
              ? source
              : t('bots.page_title.bots')
          }
          title={
            <Box
              sx={{
                display: { xs: 'block', md: 'flex' },
                alignItems: 'center',
              }}
            >
              {botBuilderType == 'mcx'
                ? t('user_bot_startegy.commodity_bot_builder')
                : botBuilderType == 'crypto_india'
                ? t('user_bot_startegy.crypto_india_bot_builder')
                : botBuilderType == 'crypto_global'
                ? t('user_bot_startegy.crypto_global_bot_builder')
                : t('user_bot_startegy.page_title3')}
              {botBuilderType == 'mcx' && <BetaChip />}
              <Box
                sx={{
                  display: 'flex',
                  mt: { xs: 1, md: 0 },
                  ml: { xs: 0, md: 1 },
                  gap: 1,
                  flexDirection: { xs: 'column', md: 'row' },
                }}
              >
                <Box
                  display={'flex'}
                  flexDirection={{ xs: 'column', md: 'row' }}
                  rowGap={1}
                >
                  <Box display={'flex'}>
                    <FeatureFlag
                      features={[FEATURES.IS_TEMPLATES]}
                      fallback={<></>}
                    >
                      <MuiButton
                        variant="contained"
                        color="secondary"
                        startIcon={<AutoAwesomeMotionIcon />}
                        onClick={() => history.push('/templates?type=option')}
                        // sx={{ width: { xs: '65%', md: 'auto' } }}
                        sx={{ flex: { xs: 5, md: 'auto' }, mr: 1 }}
                      >
                        {t('buttons.use_bot_templates')}
                      </MuiButton>
                    </FeatureFlag>
                    {isHelpDeskInSb && isLoggedIn ? (
                      <>
                        <MuiButton
                          variant="contained"
                          color="secondary"
                          startIcon={<CoPresent />}
                          // startIcon={<AutoStoriesIcon />}
                          onClick={() => setOpenCreateTicketDialog(true)}
                          sx={{ flex: { xs: 5, md: 'auto' }, mr: 1 }}
                        >
                          {t('buttons.develop_with_experts')}
                        </MuiButton>
                      </>
                    ) : (
                      <> </>
                    )}
                  </Box>
                  {isImportExportStrategy && (
                    <MuiButton
                      variant="contained"
                      color="secondary"
                      startIcon={<SaveAltOutlined />}
                      // startIcon={<AutoStoriesIcon />}
                      onClick={() => setOpenImportDialog(true)}
                      sx={{ flex: { xs: 5, md: 'auto' }, mr: 1 }}
                    >
                      Import Strategy
                    </MuiButton>
                  )}
                  <Box
                    display={'flex'}
                    sx={{
                      backgroundColor:
                        theme.component.circularProgressBar.trailColor,
                      flex: { xs: 5, md: 'auto' },
                      mr: 1,
                      pl: 1,
                      borderRadius: 1,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        alignSelf: 'center',
                        // width: { xs: '15%', md: 'auto' },
                        // flex: 'auto',
                      }}
                    >
                      {isLgUp
                        ? t('buttons.how_to_create')
                        : `${t('menu.help')}?`}
                    </Typography>
                    {/* <MuiButton
                  variant="contained"
                  color="secondary"
                  startIcon={<YouTubeIcon />}
                  onClick={() => setOpen(true)}
                  sx={{ width: { xs: '100%', md: 'auto' } }}
                /> */}
                    <IconButton
                      // sx={{ width: { xs: '10%', md: 'auto' } }}
                      // sx={{ flex: 'auto' }}
                      onClick={() => setOpen(true)}
                    >
                      <YouTubeIcon />
                    </IconButton>
                    <IconButton
                      // sx={{ width: { xs: '10%', md: 'auto' } }}
                      // sx={{ flex: 'auto' }}
                      onClick={() => setOpenReadDialog(true)}
                    >
                      <AutoStoriesIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Box>
          }
          note={isLgUp ? '' : t('user_bot_startegy.info_message')}
          buttonHandleClick={() =>
            history.replace({ pathname: '/bots', search: params.toString() })
          }
          breadcrumb={
            <Breadcrumbs
              page={PAGES.BOT_STRATEGY_BUILDER}
              data={{
                subId,
                botId: userBotDetails?.strategy_id,
                userBotId: botId,
                botName: userBotDetails?.name,
              }}
            />
          }
        />
      </Box>
      <Box sx={{ height: '100%' }}>
        {!isStockSymbolLoaded && (
          <OptionStrategyBuilderForm
            isTempalte={isTemplate}
            setUserData={setUserBotData}
            userData={
              userBotDetails && userBotDetails.sb_strategy
                ? userBotDetails
                : null
            }
            botBuilderType={botBuilderType}
          />
        )}
      </Box>

      <HelpGuideDialog
        handleClose={(val: boolean) => setOpen(val)}
        openReadDialog={open}
        url={
          siteInfo && siteInfo.option_youtube
            ? siteInfo.option_youtube
            : 'https://www.youtube.com/embed/VFJtu0l_YRY'
        }
        title="YouTube video player"
        height="560px"
      />
      <Box>
        <HelpGuideDialog
          handleClose={(val: boolean) => setOpenReadDialog(val)}
          openReadDialog={openReadDialog}
          url={
            siteInfo && siteInfo.option_builder_toolkit_link
              ? siteInfo.option_builder_toolkit_link
              : 'https://help.speedbot.tech/Options-Strategy-Builder-Toolkit/'
          }
          title="Reader Dialog"
          height="760px"
        />
      </Box>
      <CreateTickFormModal
        open={openCreateTicketDialog}
        handleClose={(val: boolean) => {
          setOpenCreateTicketDialog(val);
        }}
        isRequireToRedirect={true}
        subject="Custom Option Strategy Development with Expert"
      />
      <ImportStrategyDialog
        open={openImpmortDialog}
        handleClose={(val: boolean) => setOpenImportDialog(val)}
        botBuilderType={botBuilderType}
      />
    </Box>
  );
}
